import { addFieldEditor } from "aldoo-ra/CMS/field-editor-resolver"
import { PlatformProductType } from "../PlatformProduct"
import { SubscriptionPhaseType } from "../SubscriptionPhase"
import PriceModelEditor from "./editors/price-model-editor"
import CompactPriceEditor from "./editors/compact-price-editor"

export const PriceModelType = {
    id: "CurrencyConfig",
    label: "Currency Configuration",
    itemEditor: "CompactPriceEditor",
    groups: [
        {
            id: "config",
            label: "Configuration",
            description: "Basic configuration for the store price.",
            expanded: true,
        },
        {
            id: "platform",
            label: "Platforms",
            description: "Add platform-specific product IDs",
            expanded: true,
        },
        {
            id: "payment",
            label: "Payment",
            description: "Configure pricing and payment model",
            expanded: true
        }
    ],
    fields: [
        {
            id: "primary",
            group: "config",
            label: "Primary",
            type: "Boolean",
            description: "This is the price that is currently active on the 3rd party store.",
            render: { variant: "full" },
        },
        {
            id: "type",
            type: "Text",
            default: "real",
            visibleIf: () => false
        },
        {
            id: "real_product_id",
            group: "platform",
            label: "Platform Product IDs",
            type: "Array",
            description: "Platform-specific product IDs",
            item: PlatformProductType,
            render: { variant: "full" },
            editor: "PlatformProductEditor",
        },

        // Pricing fields (hidden from UI, handled by regular phase in phases editor)
        {
            id: "amount",
            type: "Number",
            required: true,
            visibleIf: () => false
        },
        {
            id: "currency",
            type: "Text",
            required: true,
            visibleIf: () => false
        },

        // Subscription duration configuration (hidden, handled by phases)
        {
            id: "duration",
            type: "Text",
            default: "one-time-payment",
            visibleIf: () => false
        },
        {
            id: "duration_in_days",
            type: "Text",
            visibleIf: () => false
        },
        {
            id: "auto_renew",
            type: "Boolean",
            visibleIf: () => false
        },

        // Legacy trial fields (shown when phases are not used)
        {
            id: "trial_duration",
            group: "payment",
            label: "Trial Duration",
            type: "Text",
            description: "Trial duration (for non-Web platforms)",
            options: ["no-trial", "week", "month", "year", "custom"],
            default: "no-trial",
            render: { menuPosition: "top" },
            visibleIf: (fieldValues) => {
                // Hide if using phases (Web platform with subscription)
                const duration = typeof fieldValues?.duration === 'object'
                    ? fieldValues.duration.value
                    : fieldValues?.duration

                if (duration === 'one-time-payment') return false

                const productIds = fieldValues?.real_product_id
                if (!productIds || !Array.isArray(productIds)) return true

                // Hide if Web platform is present (use phases instead)
                const hasWeb = productIds.some(product => {
                    const platform = typeof product?.platform === 'object'
                        ? product.platform.value
                        : product?.platform
                    return platform?.toLowerCase() === 'web'
                })

                return !hasWeb
            }
        },
        {
            id: "trial_duration_in_days",
            group: "payment",
            label: "Trial Duration In Days",
            type: "Text",
            description: "Enter the trial duration in days",
            visibleIf: {
                trial_duration: { in: ["custom"] }
            }
        },
        {
            id: "requires_payment_method",
            group: "payment",
            label: "Require Payment Method",
            type: "Boolean",
            description: "Require payment method before starting a trial",
            default: true,
            visibleIf: {
                trial_duration: { nin: ["no-trial"] }
            }
        },

        // Phases editor (Web/Stripe only, for subscriptions)
        {
            id: "phases",
            group: "payment",
            label: "Phases",
            type: "Array",
            description: "Configure trial, introductory offers, and regular pricing using Stripe Subscription Schedules. Available only for Web platform subscriptions.",
            item: SubscriptionPhaseType,
            render: { variant: "full" },
            editor: "PhaseSlotEditor",
            visibleIf: (fieldValues) => {
                // Show only if there's a Web platform product AND it's not a one-time payment

                // Check duration is not one-time-payment
                const duration = typeof fieldValues?.duration === 'object'
                    ? fieldValues.duration.value
                    : fieldValues?.duration

                if (duration === 'one-time-payment') return false

                // Check if real_product_id array contains a Web platform
                const productIds = fieldValues?.real_product_id
                if (!productIds || !Array.isArray(productIds) || productIds.length === 0) {
                    return false
                }

                return productIds.some(product => {
                    const platform = typeof product?.platform === 'object'
                        ? product.platform.value
                        : product?.platform
                    return platform?.toLowerCase() === 'web'
                })
            }
        }
    ]
};

// Register the currency config editors to the field editor resolver
addFieldEditor("PriceModelEditor", PriceModelEditor);
addFieldEditor("CompactPriceEditor", CompactPriceEditor);

// Note: PriceModel is an embedded type used within StorePrice
// So it doesn't need RegisterInDBResolver = true